import {identity} from 'lodash';
import React, {ReactElement, useRef} from 'react';
import styled, {css} from 'styled-components';

import {buildHoverParentClassName} from '../../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {RendererOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {getTimeFormat} from '../../../../../libs/shared/core/src/store/data/preferences/commonPreferencesSelectors';
import {BoxedIcon} from '../../../../../libs/shared/ui/src/components/icon/boxedIcon';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {
  StyledActionsDiv,
  StyledActionsIcon,
  StyledBlurbDiv,
  StyledDetailsContainerDiv,
  StyledNameDiv,
} from '../../../../../libs/shared/ui/src/components/tables/cells/commonCellStyles';
import {TableCell} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCell';
import {
  TableCellPropsWithAvatar,
  TableCellStyleProps,
} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCellTypes';
import {
  getCellBackgroundColor,
  getCellIndexStyles,
  getCellTextColor,
  getTableCellAvatarSize,
} from '../../../../../libs/shared/ui/src/components/tables/helpers/tableCellHelpers';
import {virtualTableRowBorderRadius} from '../../../../../libs/shared/ui/src/components/tables/helpers/tableHelpers';
import {getWidthSettingsStyles} from '../../../../../libs/shared/ui/src/components/tables/tableColumn';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {buildCustomSnoozePresetDescription} from '../../../../helpers/models/snoozePresetHelpers';
import {CustomSnoozeModel} from '../../../../models/api/customSnoozeModel';

/*
 * Props.
 */

interface SnoozeCellProps extends TableCellPropsWithAvatar<CustomSnoozeModel> {
  renderActionsDropdown: RendererOf<{
    modalProps: PopoverTransferProps;
    value: CustomSnoozeModel | undefined;
  }>;
  onActionsDropdownOpened: (arg: boolean) => void;
  hasHover: boolean;
}

/*
 * Styles.
 */

interface StyledSnoozeDivProps {
  $avatarSize: number;
}

const StyledSnoozeIconContainerDiv = styled.div<StyledSnoozeDivProps>`
  grid-area: icon;
`;

const StyledSnoozeCellContainerDiv = styled.div<TableCellStyleProps>`
  padding: 0 12px;
  overflow: hidden;
  white-space: nowrap;
  display: grid;
  flex: 1;
  height: 100%;
  align-items: center;

  grid-template-areas: 'icon . label . actions';
  grid-template-columns: auto 12px 1fr 7px auto;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )}
  ${(p) => getCellTextColor(p.$isSelected)};
  ${(p) =>
    !p.$shouldRemoveBottomBorderRadius &&
    css`
      border-bottom-right-radius: ${virtualTableRowBorderRadius}px;
    `}
  ${(p) =>
    !p.$shouldRemoveTopBorderRadius &&
    css`
      border-top-right-radius: ${virtualTableRowBorderRadius}px;
    `}
`;

/*
 * Component.
 */

export const SnoozeCell = (props: SnoozeCellProps): ReactElement => {
  const theme = useUserTheme();
  const {isSelected, isHovering, isLoading, size, settings, index, avatarSize, shouldShowStickyGradient} =
    props;

  const timeFormat = useAppSelector(getTimeFormat);

  const avatarSizePx = getTableCellAvatarSize(size, avatarSize);

  const renderName = (value: CustomSnoozeModel) => <StyledNameDiv>{value.name}</StyledNameDiv>;
  const renderDate = (value: CustomSnoozeModel) => buildCustomSnoozePresetDescription(value, timeFormat);
  const iconRef = useRef<HTMLDivElement>(null);

  return (
    <TableCell<CustomSnoozeModel>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveBottomBorderRadius={props.shouldRemoveBottomBorderRadius}
      shouldRemoveTopBorderRadius={props.shouldRemoveTopBorderRadius}
      renderCell={(value) => (
        <StyledSnoozeCellContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveBottomBorderRadius={props.shouldRemoveBottomBorderRadius}
          $shouldRemoveTopBorderRadius={props.shouldRemoveTopBorderRadius}
          className={buildHoverParentClassName(props.hasHover)}
        >
          <StyledSnoozeIconContainerDiv $avatarSize={avatarSizePx}>
            <BoxedIcon
              name="snoozeFilledSmall"
              color={theme.greys.shade80}
              borderColor={theme.greys.shade30}
              borderStyle="solid"
              size={size}
            />
          </StyledSnoozeIconContainerDiv>
          <StyledDetailsContainerDiv>
            {renderName(value)}
            <StyledBlurbDiv $isSelected={Boolean(isSelected)}>{renderDate(value)}</StyledBlurbDiv>
          </StyledDetailsContainerDiv>
          <StyledActionsDiv $isSelected={props.isSelected} $hasHover={Boolean(props.isHovering)}>
            <StyledActionsIcon
              label={<Trans id="settingstable-actions">Actions</Trans>}
              iconName="moreVertical"
              color={props.isSelected ? theme.greys.white : theme.greys.shade70}
              hoverActiveColor={props.isSelected ? theme.greys.white : theme.greys.shade80}
              hoverColor={props.isSelected ? theme.greys.white : theme.greys.shade80}
              isOpen={props.hasHover}
              hasHover={Boolean(props.hasHover)}
              onClick={(e) => {
                e.preventDefault();
                props.onActionsDropdownOpened(true);
              }}
              ref={iconRef}
            />
            {props.hasHover &&
              props.renderActionsDropdown({
                modalProps: {
                  anchor: iconRef,
                  onRequestClose: () => props.onActionsDropdownOpened(false),
                  wrap: identity,
                },
                value: props.value,
              })}
          </StyledActionsDiv>
        </StyledSnoozeCellContainerDiv>
      )}
    />
  );
};
